<template lang="pug">
#resident-group-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 利用者グループ情報修正

    .box-body.register-body
      FormInput(
        key-name="グループ名"
        required
        name="groupname"
        v-model="name"
        type="text"
        placeholder="グループ名"
        :maxlength="32"
        :validation-rules="{ required: true, max: 32 }"
        :styles="{ width: '340px' }")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoResidentGroupList" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="利用者グループ情報を更新しますか？" @ok="updateResidentGroup")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoResidentGroupList")

  Loading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import { PcUrl } from "@/commons/constants/PcUrl";
import CustomValidate from "@/commons/utils/CustomValidate";
import ResidentGroupsService, { ResidentGroupsGetModel, ResidentGroupsPatchModel } from "@/commons/services/ResidentGroups";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import Loading from "@/pc/basics/Loading.vue";

/**
 * 利用者グループ修正画面コンポーネント
 */
@Component({
  components: {
    FormInput,
    Loading,
    ValidationAlert,
    OkCancelModal,
    OkModal
  },
  $_veeValidate: { validator: "new" }
})
export default class ResidentGroupEdit extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** グループ名 */
  name: string = "";
  /** 整合性チェック用の更新日時 */
  updated: number = 0;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    const residentGroup: ResidentGroupsGetModel = await this.getResidentGroup();
    this.name = residentGroup.name;
    this.updated = residentGroup.updated;

    return;
  }

  /**
   * ### 編集対象の利用者グループ取得
   */
  public async getResidentGroup(): Promise<ResidentGroupsGetModel> {
    const response: ResidentGroupsGetModel = await this.residentGroupsService.get(userStore, Number(this.$route.params.id));
    return response;
  }

  /**
   * ### 更新確認ダイアログを表示する
   *
   * - バリデーションを実施する
   * - エラーでなければ確認ダイアログを表示する
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const groupNameValidationResult: boolean = await this.validateResidentGroupName(Number(this.$route.params.id), this.name);

    if (validationResult && groupNameValidationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 利用者グループ名バリデーション
   */
  public async validateResidentGroupName(groupId: number, groupName: string): Promise<boolean> {
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    for (const group of groups) {
      if (group.id != groupId && group.name == groupName) {
        this.errorMessage = "既に使用されている利用者グループ名には変更できません";
        return false;
      }
    }

    return true;
  }

  /**
   * ### バリデーションチェック用の利用者グループ取得
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループを更新する
   *
   * - ローディングを表示
   * - 利用者グループサービスを使用して利用者グループを更新する
   * - 例外が無ければ成功ダイアログを表示する
   */
  public async updateResidentGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const groupId: number = Number(this.$route.params.id);
      const residentGroup: ResidentGroupsPatchModel = this.createUpdateParam();
      await this.residentGroupsService.patch(userStore, groupId, residentGroup);

      // 利用者一覧を再読み込みさせるようstoreを更新
      let residentList: ResidentListState = needRefreshesStore.residentList;
      residentList.residentList = true;
      needRefreshesStore.setResidentList(residentList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 更新成功ダイアログを表示する
      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * 利用者グループ更新パラメータ作成
   */
  public createUpdateParam(): ResidentGroupsPatchModel {
    const residentGroup: ResidentGroupsPatchModel = {
      name: this.$stringUtil.convertYen(this.name),
      updated: this.updated
    };

    return residentGroup;
  }

  /**
   * ### 利用者グループ一覧画面に遷移する
   */
  public gotoResidentGroupList(): void {
    this.$router.push(PcUrl.ResidentGroupList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#resident-group-edit {
  max-width: 740px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
